/**
 * What a tap on a thing you own writes into the action box.
 *
 * The point is not the wording, it is the loop: *"I have a thing"* and *"I do a thing with the
 * thing"* are the two halves of an inventory RPG, and they used to be unjoined — the pack was a
 * list you read and the action box was a blank line you filled in from memory. One tap makes
 * the pack the menu it always looked like.
 *
 * **These are openings, not commands.** The text lands in the box with the caret at its end
 * and nothing is submitted (`turn/draft.ts`), so the player finishes the sentence — *"use the
 * field dressing"* → *"use the field dressing on the laceration, then get off the road"*. That
 * is why a merely plausible verb is good enough and a wrong one costs nothing: the worst case
 * is a word the player deletes, not a turn spent on the client's guess.
 *
 * **The verb comes from U5's category.** It is the only thing on the wire that distinguishes a
 * revolver from a brass token, and picking the verb is the second thing that field buys after
 * the headings — *"ready the service revolver"* and *"take a closer look at the market token"*
 * are both better than one generic *"use"*, and neither is derivable from `ammo`.
 *
 * **These strings are read twice**: by the player on the button, and by the DM once the button
 * has written them into the action box. That makes them the one place in the client where a
 * translation is also model input, and it is the sharpest reason the whole play surface renders
 * in the *story's* locale rather than the reader's (`i18n/I18nProvider.tsx`). A German sentence
 * submitted into an English story would reach a DM that was told to write English.
 */
import type { Injury, InventoryItem, ItemCategory } from '@/api/generated';
import type { UiKey } from '@/i18n/en.generated';
import type { Translate } from '@/i18n/t';

/**
 * Exhaustive over `ItemCategory`, so a category the API adds is a typecheck error rather than
 * an item that silently stops offering an action (D5).
 *
 * `misc` gets the odd one out because trade goods and keepsakes have no obvious use, and that
 * is the interesting thing about them — the DM knows what the brass disc is for and the player
 * does not, so the opening is a question rather than a use.
 */
const VERB_KEYS: Record<ItemCategory, UiKey> = {
  weapon: 'ui.action.item.weapon',
  consumable: 'ui.action.item.consumable',
  gear: 'ui.action.item.gear',
  misc: 'ui.action.item.misc',
};

export function itemAction(t: Translate, entry: InventoryItem): string {
  return t(VERB_KEYS[entry.category], { item: nameInSentence(t, entry.display_name) });
}

/**
 * *"tend to my left forearm"* — `location` is the DM's own free text for where it hurts.
 *
 * True since L8; until then `add_injury` asked for `'left_arm'` and this button offered the
 * player a snake_case key to submit. It is also why the German template moved the slot behind
 * a colon: *"meine Verletzung an {location} versorgen"* put DM-written prose under a
 * preposition governing the dative, and nothing here can decline what the model wrote — the
 * same rule the backend's `changes.*` templates follow (`quest/api/display.py`).
 */
export function injuryAction(t: Translate, injury: Injury): string {
  return t('ui.action.injury', { location: injury.location });
}

/**
 * A display name mid-sentence, spelled exactly as the DM wrote it.
 *
 * **Nothing here inspects the characters of the name.** Until 2026-08-19 this tested
 * `/[A-Z]/` against everything after the first letter, on the theory that an inner capital
 * means the DM *named* the thing (*"Ferron's Ledger"*) rather than described it (*"Rusty
 * knife"*), and lowercased the first character when the test failed. The file's own comment
 * claimed that was "a fact about the name rather than about the language". It is not: `[A-Z]`
 * is Latin-only, so Ukrainian *«Ржавий Ніж»* failed the proper-noun test and was lowercased,
 * and in German lowercasing a noun's first letter is simply a spelling error — on every item,
 * in every suggestion button. This was the client's one violation of the no-string-matching
 * rule (`../../CLAUDE.md` §7), and it is the rule's whole shape in miniature: a measurable
 * property of text a model wrote is still a guess about it, and the guess was settled
 * silently — wrong in two of the three live locales and reported by nothing.
 *
 * All that is left is the **article**, which is the template's and always was:
 * `ui.action.the_item` is `the {item}` in English and a bare `{item}` in Ukrainian, which has
 * no articles. It is cosmetic either way — the player is about to edit the line, and a wrong
 * article costs a backspace, where a misspelled name is wrong on the button *and* in what the
 * DM is handed.
 *
 * If *"the Ferron's Ledger"* ever grates enough to fix, the fix is a field on the item rather
 * than a test on its spelling: the DM knows whether it named a thing or described one, so it
 * can say so.
 */
function nameInSentence(t: Translate, name: string): string {
  if (name.length === 0) return name;
  return t('ui.action.the_item', { item: name });
}
